import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import { useCreateClient } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/** Minimal modal for registering a new client. After success, shows the
 * one-time shared_secret (operator must copy it before closing — it's the
 * only chance). */
export function NewClientDialog({ open, onOpenChange }: Props) {
	const create = useCreateClient();
	const [slug, setSlug] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [portalUrl, setPortalUrl] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [secret, setSecret] = useState<string | null>(null);

	function reset() {
		setSlug("");
		setDisplayName("");
		setPortalUrl("");
		setContactEmail("");
		setSecret(null);
		create.reset();
	}

	function onClose() {
		reset();
		onOpenChange(false);
	}

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		try {
			const result = await create.mutateAsync({
				slug,
				display_name: displayName,
				portal_url: portalUrl,
				contact_email: contactEmail || null,
			});
			setSecret(result.shared_secret);
		} catch {
			// surfaced via create.error state
		}
	}

	if (!open) return null;

	const errorMessage =
		create.error instanceof ApiError
			? create.error.detail
			: create.error
				? "Unable to create client."
				: null;

	return (
		// Custom overlay — not using <dialog> because its modal behaviour
		// requires imperative showModal() which fights with React state.
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
			// biome-ignore lint/a11y/useSemanticElements: <dialog> needs imperative API; see comment above
			role="dialog"
			aria-modal="true"
			aria-labelledby="new-client-title"
		>
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle id="new-client-title">
						{secret ? "Copy the shared secret now" : "New client"}
					</CardTitle>
					<CardDescription>
						{secret
							? "This is shown ONCE. Paste it into the client portal's AGGREGATOR_SHARED_SECRET env var."
							: "Register a per-tenant portal. The aggregator will poll its /aggregator/health."}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{secret ? (
						<>
							<pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">
								{secret}
							</pre>
							<div className="flex justify-end gap-2">
								<Button variant="outline" onClick={() => navigator.clipboard.writeText(secret)}>
									Copy
								</Button>
								<Button onClick={onClose}>Done</Button>
							</div>
						</>
					) : (
						<form className="space-y-3" onSubmit={onSubmit} noValidate>
							<div className="space-y-1">
								<Label htmlFor="slug">Slug</Label>
								<Input
									id="slug"
									pattern="^[a-z0-9][a-z0-9-]*$"
									required
									placeholder="acme"
									value={slug}
									onChange={(e) => setSlug(e.target.value)}
								/>
								<p className="text-xs text-muted-foreground">
									Lowercase letters, digits, hyphens. Matches the portal's Tenant.slug.
								</p>
							</div>
							<div className="space-y-1">
								<Label htmlFor="display-name">Display name</Label>
								<Input
									id="display-name"
									required
									placeholder="Acme Law"
									value={displayName}
									onChange={(e) => setDisplayName(e.target.value)}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="portal-url">Portal URL</Label>
								<Input
									id="portal-url"
									type="url"
									required
									placeholder="https://acme.veridonconsulting.com"
									value={portalUrl}
									onChange={(e) => setPortalUrl(e.target.value)}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="contact-email">Contact email (optional)</Label>
								<Input
									id="contact-email"
									type="email"
									placeholder="admin@acme.com"
									value={contactEmail}
									onChange={(e) => setContactEmail(e.target.value)}
								/>
							</div>
							{errorMessage ? (
								<p className="text-sm text-destructive" role="alert">
									{errorMessage}
								</p>
							) : null}
							<div className="flex justify-end gap-2 pt-2">
								<Button type="button" variant="outline" onClick={onClose}>
									Cancel
								</Button>
								<Button type="submit" disabled={create.isPending}>
									{create.isPending ? "Creating…" : "Create"}
								</Button>
							</div>
						</form>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
